import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { X, Sparkles, Download, Share2, Loader2 } from "lucide-react";
import { generateMilestoneImage } from "@/lib/milestoneCard";

export const MILESTONES = [
  { days: 1, label: "1 jour", title: "Premier jour !", desc: "Le début est le pas le plus important. Tu l'as fait.", tier: "Premier pas", emoji: "✨", color: "#CBD5E1", gradient: "from-slate-400 to-slate-200" },
  { days: 3, label: "3 jours", title: "Trois jours de courage", desc: "Les premières 72 heures, souvent les plus dures. Chapeau.", tier: "Bronze", emoji: "🥉", color: "#CD7F32", gradient: "from-amber-700 to-amber-500" },
  { days: 7, label: "1 semaine", title: "Une semaine !", desc: "Sept jours entiers. Ton corps commence déjà à te remercier.", tier: "Argent", emoji: "🥈", color: "#C0C0C0", gradient: "from-slate-400 to-slate-200" },
  { days: 14, label: "2 semaines", title: "Deux semaines solides", desc: "L'habitude s'installe. Tu construis quelque chose.", tier: "Argent étoilé", emoji: "⚔️", color: "#94A3B8", gradient: "from-slate-500 to-sky-200" },
  { days: 30, label: "1 mois", title: "Un mois complet", desc: "Un cap énorme. Prends un instant pour mesurer le chemin.", tier: "Or", emoji: "🥇", color: "#FFD700", gradient: "from-amber-500 to-yellow-300" },
  { days: 90, label: "3 mois", title: "Trois mois, incroyable", desc: "C'est officiel : tu as changé une partie de toi.", tier: "Platine", emoji: "💎", color: "#A7F3D0", gradient: "from-emerald-400 to-cyan-300" },
  { days: 180, label: "6 mois", title: "Six mois de résilience", desc: "La moitié d'une année. Tu es la preuve que c'est possible.", tier: "Diamant", emoji: "💠", color: "#60A5FA", gradient: "from-sky-500 to-indigo-400" },
  { days: 365, label: "1 an", title: "UNE ANNÉE ENTIÈRE", desc: "Un an. Mille fois bravo. Garde ce moment en mémoire.", tier: "Légende", emoji: "🏆", color: "#F97316", gradient: "from-orange-500 via-rose-400 to-emerald-400" },
];

function daysSince(iso) {
  const start = new Date(iso).getTime();
  const now = Date.now();
  return Math.floor((now - start) / 86400000);
}

const CONFETTI_COLORS = ["#F97316", "#10B981", "#F43F5E", "#38BDF8", "#F59E0B", "#A78BFA"];

function Confetti() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 60 }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        delay: Math.random() * 0.4,
        duration: 2 + Math.random() * 2,
        rotate: Math.random() * 360,
        size: 6 + Math.random() * 8,
      })),
    []
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((p) => (
        <motion.span
          key={p.id}
          initial={{ y: -20, opacity: 0, rotate: 0 }}
          animate={{ y: "110%", opacity: [0, 1, 1, 0], rotate: p.rotate }}
          transition={{ duration: p.duration, delay: p.delay, ease: "easeIn" }}
          style={{
            position: "absolute",
            left: `${p.left}%`,
            width: p.size,
            height: p.size * 0.4,
            background: p.color,
            borderRadius: 2,
          }}
        />
      ))}
    </div>
  );
}

export default function MilestoneCelebration({ data, onDismiss }) {
  const [currentMilestone, setCurrentMilestone] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [blob, setBlob] = useState(null);

  useEffect(() => {
    if (!data) return;
    const d = daysSince(data.start_iso);
    const last = data.last_celebrated_days || 0;
    const next = MILESTONES.filter((m) => m.days <= d && m.days > last).pop();
    if (next) {
      setCurrentMilestone(next);
    }
  }, [data]);

  // Generate the shareable card as soon as a milestone appears
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!currentMilestone || !data) return;
      setGenerating(true);
      try {
        const b = await generateMilestoneImage({
          milestone: currentMilestone,
          counterTitle: data.title,
        });
        if (cancelled) return;
        setBlob(b);
        setPreviewUrl(URL.createObjectURL(b));
      } catch (e) {
        if (!cancelled) toast.error("Impossible de générer la carte image.");
      } finally {
        if (!cancelled) setGenerating(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [currentMilestone, data]);

  // Clean the object URL when it changes / closes
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const close = () => {
    if (currentMilestone) {
      onDismiss(currentMilestone.days);
      setCurrentMilestone(null);
      setBlob(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
    }
  };

  const download = () => {
    if (!blob || !currentMilestone) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `palier-${currentMilestone.days}j-equipe-mental.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Image téléchargée.");
  };

  const share = async () => {
    if (!currentMilestone) return;
    const text = `Je viens de franchir ${currentMilestone.label} sur mon compteur « ${data.title} » grâce à L'Équipe & Mental 💛`;
    try {
      if (blob && navigator.canShare && navigator.canShare({ files: [new File([blob], "palier.png", { type: "image/png" })] })) {
        const file = new File([blob], `palier-${currentMilestone.days}j.png`, { type: "image/png" });
        await navigator.share({
          title: "Palier franchi !",
          text,
          files: [file],
        });
        return;
      }
      if (navigator.share) {
        await navigator.share({ title: "Palier franchi !", text });
        return;
      }
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        toast.success("Message copié. Partage-le où tu veux !");
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        toast("Partage annulé.");
      }
    }
  };

  return (
    <AnimatePresence>
      {currentMilestone && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md grid place-items-center p-5 overflow-auto"
          data-testid="milestone-modal"
          onClick={(e) => e.target === e.currentTarget && close()}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 18, stiffness: 180 }}
            className="relative em-card max-w-2xl w-full p-6 lg:p-8 overflow-hidden my-6"
            style={{
              background:
                "radial-gradient(500px 300px at 50% 0%, rgba(249, 115, 22, 0.25), transparent 60%), radial-gradient(400px 300px at 20% 100%, rgba(16, 185, 129, 0.18), transparent 60%), linear-gradient(180deg, rgba(30, 41, 59, 0.98), rgba(15, 23, 42, 0.98))",
            }}
          >
            <Confetti />
            <button
              onClick={close}
              aria-label="Fermer"
              className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 z-10"
            >
              <X className="w-4 h-4 text-slate-300" />
            </button>

            <div className="relative z-10 text-center">
              <motion.div
                initial={{ scale: 0, rotate: -20 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.15, type: "spring", stiffness: 200 }}
                className={`mx-auto w-24 h-24 rounded-full bg-gradient-to-br ${currentMilestone.gradient} grid place-items-center shadow-xl`}
                style={{ boxShadow: `0 0 50px ${currentMilestone.color}55` }}
              >
                <span className="text-5xl">{currentMilestone.emoji}</span>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-5 em-eyebrow"
              >
                Badge {currentMilestone.tier} débloqué · {currentMilestone.label}
              </motion.p>
              <motion.h3
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="mt-2 font-[Outfit] font-bold text-3xl lg:text-4xl text-white tracking-tight"
                data-testid="milestone-title"
              >
                {currentMilestone.title}
              </motion.h3>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.42 }}
                className="mt-3 text-slate-200 leading-relaxed max-w-md mx-auto"
              >
                {currentMilestone.desc}
              </motion.p>

              {/* Preview of the shareable card */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-6 mx-auto max-w-xs rounded-2xl overflow-hidden border border-white/10 bg-black/40"
                data-testid="milestone-card-preview"
              >
                {generating || !previewUrl ? (
                  <div className="aspect-square grid place-items-center text-slate-400 text-sm">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Création de la carte…
                    </div>
                  </div>
                ) : (
                  <img
                    src={previewUrl}
                    alt="Carte de palier à partager"
                    className="w-full aspect-square object-cover"
                  />
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3"
              >
                <button
                  onClick={download}
                  disabled={!blob || generating}
                  data-testid="milestone-download"
                  className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-semibold px-5 py-3 rounded-full"
                >
                  <Download className="w-4 h-4" />
                  Télécharger la carte
                </button>
                <button
                  onClick={share}
                  disabled={generating}
                  data-testid="milestone-share"
                  className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-5 py-3 rounded-full"
                >
                  <Share2 className="w-4 h-4" />
                  Partager
                </button>
                <button
                  onClick={close}
                  data-testid="milestone-close"
                  className="text-sm text-slate-400 hover:text-slate-200 px-3 py-2"
                >
                  Je savoure, fermer
                </button>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
